import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { Chat } from "../reducers/chatsSlice"
import useChatInfo from "../hooks/useChatInfo"
import AvatarImage from "./AvatarImage"
import CarConfigSelector, { CarConfigSelectorHandle } from "./CarConfigSelector"
import { XmppContext } from "../stream/xmppClient"
import { useAppSelector } from "../reducers/hooks"
import { selectChatSubject } from "../reducers/subjectsSlice"
import useCarRouteConfig from "../hooks/useCarRouteConfig"
import AdHocCommands, { CarRouteConfig } from "../api/adHocCommands"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react"
import XmppGroupChatApi from "@/api/xmppGroupChatApi"
import { selectRouteJidFromChatJid } from "@/reducers/selectors"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import VehicleTypePresetSelector from "@/components/VehicleTypePresetSelector"
import { useDriverRoomIssues } from "@/hooks/useDriverRoomIssues"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"

type ChatInfoProp = {
  chat: Chat
  isOpen: boolean
  onClose: () => void
}

const ChatInfo = ({ chat, isOpen, onClose }: ChatInfoProp) => {
  const { t } = useTranslation()
  const { chatTitle, photoSrc, additionalInfo } = useChatInfo(chat)
  const subject = useAppSelector(selectChatSubject(chat.jid))
  const { client } = useContext(XmppContext)
  const [isSaving, setIsSaving] = useState(false)

  const routeJid = useAppSelector(selectRouteJidFromChatJid(chat.jid))
  const accountChat = useAppSelector(selectAccountChatByJid(chat.jid))
  const {
    carRouteConfig,
    isLoading: isLoadingConfig,
    refreshCarRouteConfig,
  } = useCarRouteConfig(client, routeJid)

  const issues = useDriverRoomIssues(chat.jid)

  // Local state for pending changes
  const [pendingDescription, setPendingDescription] = useState<string>("")
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const [hasCarConfigChanges, setHasCarConfigChanges] = useState(false)

  // Ref for CarConfigSelector
  const carConfigRef = useRef<CarConfigSelectorHandle>(null)

  const handleOnUpdateDescription = useCallback(
    async (description: string) => {
      try {
        await XmppGroupChatApi.changeSubject(client, chat.jid, description)
      } catch (e) {
        window.appToaster.show({
          message: i18n.t("app.chatInfo.descriptionError"),
          intent: "danger",
          timeout: 20,
        })
      }
    },
    [chat.jid, client],
  )

  const handleUpdateCarConfig = useCallback(
    async (config: Partial<CarRouteConfig>) => {
      if (routeJid) {
        try {
          await AdHocCommands.updateCarConfig(client, routeJid, config)
        } catch (e) {
          window.appToaster.show({
            message: i18n.t("app.chatInfo.carConfigError"),
            intent: "danger",
            timeout: 1000,
          })
        }
      }
    },
    [client, routeJid],
  )

  const handleClearCarConfig = useCallback(async () => {
    if (routeJid) {
      const emptyConfig: CarRouteConfig = {
        width: undefined,
        height: undefined,
        length: undefined,
        weight: undefined,
        travelMode: "car",
      }
      try {
        await AdHocCommands.updateCarConfig(client, routeJid, emptyConfig)
      } catch (e) {
        window.appToaster.show({
          message: i18n.t("app.chatInfo.clearCarConfigError"),
          intent: "danger",
          timeout: 1000,
        })
      }
    }
  }, [client, routeJid])

  // Initialize and sync local state
  useEffect(() => {
    setPendingDescription(subject?.text || "")
    setHasPendingChanges(false)
    setHasCarConfigChanges(false)
  }, [subject?.text, chat.jid])

  // Refresh car config when dialog opens
  useEffect(() => {
    if (isOpen && routeJid) {
      refreshCarRouteConfig()
    }
  }, [isOpen, routeJid, refreshCarRouteConfig])

  // Check if there are any pending changes
  useEffect(() => {
    const descriptionChanged = pendingDescription !== (subject?.text || "")
    setHasPendingChanges(descriptionChanged || hasCarConfigChanges)
  }, [pendingDescription, subject?.text, hasCarConfigChanges])

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setPendingDescription(event.target.value)
  }

  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true)
    if (!hasPendingChanges) return

    try {
      // Save description if changed
      if (pendingDescription !== (subject?.text || "")) {
        await handleOnUpdateDescription(pendingDescription)
      }

      // Save car config if changed
      if (carConfigRef.current && hasCarConfigChanges) {
        await carConfigRef.current.saveConfig()
      }

      setHasPendingChanges(false)
      setHasCarConfigChanges(false)
      onClose()
      await refreshCarRouteConfig()
    } catch (error) {
      console.error("Failed to save changes:", error)
    } finally {
      setIsSaving(false)
    }
  }, [
    handleOnUpdateDescription,
    hasCarConfigChanges,
    hasPendingChanges,
    onClose,
    pendingDescription,
    refreshCarRouteConfig,
    subject?.text,
  ])

  const handleCancel = useCallback(() => {
    setPendingDescription(subject?.text || "")
    setHasPendingChanges(false)
    setHasCarConfigChanges(false)
    // Reset car config to initial state by refreshing
    if (carConfigRef.current) {
      refreshCarRouteConfig()
    }
    onClose()
  }, [onClose, refreshCarRouteConfig, subject?.text])

  const handleClearAll = useCallback(async () => {
    await handleClearCarConfig()
    await handleOnUpdateDescription("")
    onClose()
  }, [handleClearCarConfig, handleOnUpdateDescription, onClose])

  // Loading spinner component
  const UpdateIndicator = () => {
    return (
      <div className="absolute left-1/2 top-1/2 z-[1000] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg p-4">
        <Loader2 size={25} className="animate-spin" />
      </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[95vh] overflow-y-auto sm:max-w-2xl"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        {(isLoadingConfig || isSaving) && <UpdateIndicator />}
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold">
            <AvatarImage name={chatTitle} src={photoSrc} size="large" />
            <div>
              <div>{chatTitle}</div>
              {additionalInfo && (
                <div className="text-sm font-normal text-muted-foreground">
                  {additionalInfo}
                </div>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          {issues && issues.hasAnyIssue && (
            <div className="mb-4 space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
              {issues.gpsNotConfigured && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>{t("app.chatInfo.gpsNotAssigned")}</span>
                </div>
              )}
              {issues.gpsNeverReceived && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>{t("app.chatInfo.gpsNeverReceived")}</span>
                </div>
              )}
              {issues.gpsStale && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>{t("app.chatInfo.gpsStale")}</span>
                </div>
              )}
              {issues.carNotConfigured && (
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>{t("app.chatInfo.carNotConfigured")}</span>
                </div>
              )}
            </div>
          )}

          {/* Description Section */}
          <div className="space-y-2">
            <Label htmlFor="description">{t("app.chatInfo.description")}</Label>
            <Textarea
              id="description"
              placeholder={t("app.chatInfo.descriptionPlaceholder")}
              value={pendingDescription}
              onChange={handleDescriptionChange}
              className="min-h-[80px] resize-none"
              disabled={isSaving || isLoadingConfig}
            />
          </div>

          {/* Car Config Section */}
          {routeJid && (
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                {t("app.chatInfo.carConfiguration")}
              </Label>
              <div className="mb-3 flex items-center gap-2">
                <Label className="text-sm font-medium">
                  {t("app.chatInfo.applyPreset")}
                </Label>
                <VehicleTypePresetSelector
                  currentConfig={carRouteConfig}
                  disabled={isLoadingConfig}
                  onApply={(preset) => {
                    carConfigRef.current?.applyConfig(preset)
                  }}
                />
              </div>
              <CarConfigSelector
                ref={carConfigRef}
                currentConfig={carRouteConfig}
                onConfigChange={handleUpdateCarConfig}
                onClearConfig={handleClearCarConfig}
                isLoading={isLoadingConfig}
                onHasChangesChange={setHasCarConfigChanges}
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between pt-6">
          <div className="flex gap-2">
            {routeJid && (
              <Button
                variant="destructive"
                onClick={handleClearAll}
                className="flex items-center gap-2"
                disabled={isSaving}
              >
                <RotateCcw className="h-4 w-4" />
                {t("app.chatInfo.clearAll")}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={!hasPendingChanges || isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {t("app.chatInfo.saveChanges")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ChatInfo
